/**
 * Change Logger Utility
 * Dokumentiert alle Änderungen im Admin-Bereich
 */

export interface ChangeLogEntry {
  id: string;
  timestamp: string;
  category: 'datenschutz' | 'dokumente' | 'betroffenenrechte' | 'backup' | 'iso27001' | 'system' | 'konfiguration';
  action: string;
  description: string;
  user?: string;
  details?: Record<string, any>;
}

const STORAGE_KEY = 'eap-admin-changelog';

/**
 * Lädt das komplette Changelog aus localStorage
 */
export const loadChangelog = (): ChangeLogEntry[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Fehler beim Laden des Changelogs:', error);
    return [];
  }
};

/**
 * Speichert das Changelog in localStorage
 */
const saveChangelog = (entries: ChangeLogEntry[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    console.error('Fehler beim Speichern des Changelogs:', error);
  }
};

/**
 * Fügt einen neuen Eintrag zum Changelog hinzu
 */
export const logChange = (
  category: ChangeLogEntry['category'],
  action: string,
  description: string,
  details?: Record<string, any>
): ChangeLogEntry => {
  const entry: ChangeLogEntry = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    category,
    action,
    description,
    details
  };

  const entries = loadChangelog();
  entries.unshift(entry); // Neueste zuerst
  saveChangelog(entries);

  return entry;
};

/**
 * Exportiert das Changelog als Text-Datei
 */
export const exportChangelog = (format: 'txt' | 'md' | 'csv' = 'txt'): void => {
  const entries = loadChangelog();
  let content = '';
  let filename = '';

  switch (format) {
    case 'md':
      content = generateMarkdown(entries);
      filename = `EAP-Changelog-${new Date().toISOString().split('T')[0]}.md`;
      break;
    case 'csv':
      content = generateCSV(entries);
      filename = `EAP-Changelog-${new Date().toISOString().split('T')[0]}.csv`;
      break;
    default:
      content = generateText(entries);
      filename = `EAP-Changelog-${new Date().toISOString().split('T')[0]}.txt`;
  }

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generiert Markdown-Format
 */
const generateMarkdown = (entries: ChangeLogEntry[]): string => {
  let md = `# EAP Admin Changelog\n\n`;
  md += `Generiert am: ${new Date().toLocaleString('de-AT')}\n\n`;
  md += `---\n\n`;

  const groupedByDate = groupByDate(entries);
  
  Object.entries(groupedByDate).forEach(([date, items]) => {
    md += `## ${date}\n\n`;
    items.forEach(entry => {
      const time = new Date(entry.timestamp).toLocaleTimeString('de-AT', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      md += `### ${time} - ${entry.action}\n\n`;
      md += `**Kategorie:** ${getCategoryLabel(entry.category)}\n\n`;
      md += `**Beschreibung:** ${entry.description}\n\n`;
      if (entry.details) {
        md += `**Details:**\n\n`;
        md += `\`\`\`json\n${JSON.stringify(entry.details, null, 2)}\n\`\`\`\n\n`;
      }
      md += `---\n\n`;
    });
  });

  return md;
};

/**
 * Generiert Text-Format
 */
const generateText = (entries: ChangeLogEntry[]): string => {
  let txt = `EAP ADMIN CHANGELOG\n`;
  txt += `${'='.repeat(80)}\n\n`;
  txt += `Generiert am: ${new Date().toLocaleString('de-AT')}\n\n`;

  entries.forEach((entry, index) => {
    txt += `${index + 1}. ${new Date(entry.timestamp).toLocaleString('de-AT')}\n`;
    txt += `   Kategorie: ${getCategoryLabel(entry.category)}\n`;
    txt += `   Aktion: ${entry.action}\n`;
    txt += `   Beschreibung: ${entry.description}\n`;
    if (entry.details) {
      txt += `   Details: ${JSON.stringify(entry.details)}\n`;
    }
    txt += `\n${'-'.repeat(80)}\n\n`;
  });

  return txt;
};

/**
 * Generiert CSV-Format
 */
const generateCSV = (entries: ChangeLogEntry[]): string => {
  let csv = 'Zeitstempel,Kategorie,Aktion,Beschreibung,Details\n';
  
  entries.forEach(entry => {
    const details = entry.details ? JSON.stringify(entry.details).replace(/"/g, '""') : '';
    csv += `"${entry.timestamp}","${getCategoryLabel(entry.category)}","${entry.action}","${entry.description}","${details}"\n`;
  });

  return csv;
};

/**
 * Gruppiert Einträge nach Datum
 */
const groupByDate = (entries: ChangeLogEntry[]): Record<string, ChangeLogEntry[]> => {
  const grouped: Record<string, ChangeLogEntry[]> = {};
  
  entries.forEach(entry => {
    const date = new Date(entry.timestamp).toLocaleDateString('de-AT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(entry);
  });

  return grouped;
};

/**
 * Gibt das deutsche Label für eine Kategorie zurück
 */
export const getCategoryLabel = (category: ChangeLogEntry['category']): string => {
  const labels: Record<ChangeLogEntry['category'], string> = {
    datenschutz: 'Datenschutz',
    dokumente: 'Dokumente',
    betroffenenrechte: 'Betroffenenrechte',
    backup: 'Backup',
    iso27001: 'ISO 27001',
    system: 'System',
    konfiguration: 'Konfiguration'
  };
  return labels[category] || category;
};

/**
 * Löscht alle Changelog-Einträge (mit Bestätigung empfohlen)
 */
export const clearChangelog = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Filtert Changelog nach Kategorie
 */
export const filterByCategory = (category: ChangeLogEntry['category']): ChangeLogEntry[] => {
  const entries = loadChangelog();
  return entries.filter(entry => entry.category === category);
};

/**
 * Filtert Changelog nach Zeitraum
 */
export const filterByDateRange = (startDate: Date, endDate: Date): ChangeLogEntry[] => {
  const entries = loadChangelog();
  return entries.filter(entry => {
    const entryDate = new Date(entry.timestamp);
    return entryDate >= startDate && entryDate <= endDate;
  });
};
